import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handle, true);
    return () => document.removeEventListener("mousedown", handle, true);
  }, [onClose]);

  const menuW = 200;
  const menuH = items.length * 24 + 8;
  const adjustedX = x + menuW > window.innerWidth ? x - menuW : x;
  const adjustedY = y + menuH > window.innerHeight ? y - menuH : y;

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.08 }}
        className="fixed z-[2000]"
        style={{
          top: adjustedY,
          left: adjustedX,
          width: menuW,
          background: "#fff",
          border: "1px solid #7a7a7a",
          boxShadow: "2px 2px 5px rgba(0,0,0,0.25)",
          padding: "2px 0",
          fontFamily: "var(--xp-font-body)",
          fontSize: 12,
        }}
      >
        {items.map((item, i) =>
          item.divider ? (
            <div
              key={i}
              style={{
                height: 1,
                background: "#d4d0c8",
                borderBottom: "1px solid #fff",
                margin: "4px 6px",
              }}
            />
          ) : (
            <button
              type="button"
              key={i}
              disabled={item.disabled}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              className={[
                "flex items-center w-full text-left px-6",
                "select-none",
                item.disabled
                  ? "text-[#808080] cursor-default"
                  : "text-black cursor-pointer hover:bg-[var(--xp-selection)] hover:text-white",
              ].join(" ")}
              style={{
                height: 24,
                background: "transparent",
                border: "none",
                fontFamily: "var(--xp-font-body)",
                fontSize: 12,
              }}
            >
              {item.label}
            </button>
          ),
        )}
      </motion.div>
    </AnimatePresence>
  );
}
